import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { ingestPdfCsvNarGrid } from "../../../../../api/postCall/ingestPdfCsvNarGrid";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { isProduction } from "../../../../../utils/runtimeEnv";
import { checkIfStringIsEmpty } from "../../../../../utils/stringModifications";
import { generateNarGcsFilePath } from "../utils/generateNarFilePath";

interface HandleNarIngestionApiCallParams {
  payerType: string;
  subType: string;
  month: number;
  year: number;
  documentType: string;
  ingest: boolean;
  day?: number;
  onSuccess?: () => void;
  setIsSubmitting?: (isSubmitting: boolean) => void;
  sourceFileName?: string;
  effectiveDate?: string;
  neverExpire?: boolean;
  startPage?: number;
  endPage?: number;
}

export const handleNarIngestionApiCall = async ({
  payerType,
  subType,
  month,
  year,
  documentType,
  ingest,
  day,
  onSuccess,
  setIsSubmitting,
  sourceFileName,
  effectiveDate,
  neverExpire = false,
  startPage,
  endPage,
}: HandleNarIngestionApiCallParams) => {
  try {
    if (setIsSubmitting) {
      setIsSubmitting(true);
    }

    // Convert to numbers and validate
    const monthNum = Number(month);
    const yearNum = Number(year);
    const dayNum = day ? Number(day) : undefined;

    // Validate that month and year are valid numbers
    if (isNaN(monthNum) || isNaN(yearNum)) {
      toast.error(
        ingest
          ? ToastMessages.NAR_INGESTION_APPROVED_FAILURE
          : ToastMessages.NAR_INGESTION_FAILURE,
      );
      if (setIsSubmitting) {
        setIsSubmitting(false);
      }
      return;
    }

    const bucketName = isProduction()
      ? "prior--backen-prod-svc-u4g8.appspot.com"
      : "prior-auth-portal-dev.appspot.com";

    const dayOfMonth = dayNum ?? 1;

    // Generate effective_date in YYYY-MM-DD format
    const effectiveDateFormatted =
      effectiveDate ??
      `${yearNum}-${String(monthNum).padStart(2, "0")}-${String(dayOfMonth).padStart(2, "0")}`;

    // Generate file path with auth_grid prefix
    const gcsFilePath = generateNarGcsFilePath(
      payerType,
      subType,
      dayOfMonth,
      monthNum,
      yearNum,
      documentType,
      bucketName,
      true, // includeAuthGrid for file_path
      false,
    );

    const gcsFileUrl = generateNarGcsFilePath(
      payerType,
      subType,
      dayOfMonth,
      monthNum,
      yearNum,
      documentType,
      bucketName,
      true,
    );

    const payload = {
      file_type: documentType,
      ingest: ingest,
      payer_type: payerType,
      effective_date: effectiveDateFormatted,
      sub_type: subType,
      version: "latest",
      artifact_creation_date: new Date().toISOString(),
      bigquery_table: "auth_grid",
      bigquery_dataset: "auth_verification",
      client_bigquery_table: "client_config",
      file_url: gcsFileUrl,
      file_path: checkIfStringIsEmpty(sourceFileName)
        ? gcsFilePath
        : sourceFileName,
      never_expire: neverExpire,
      // Explicit page range for PDF ingestion; omitted for CSV.
      ...(documentType === "pdf" && {
        ...(startPage !== undefined && { start_page: startPage }),
        ...(endPage !== undefined && { end_page: endPage }),
      }),
    };

    try {
      await Promise.race([
        ingestPdfCsvNarGrid(payload),
        new Promise((resolve) => setTimeout(resolve, 5000)).then(() => ({
          success: true,
        })),
      ]);

      if (setIsSubmitting) {
        setIsSubmitting(false);
      }
      onSuccess?.();
    } catch (error) {
      console.error("Error ingesting NAR grid:", error);
      toast.error(ToastMessages.NAR_INGESTION_FAILURE);
      if (setIsSubmitting) {
        setIsSubmitting(false);
      }
      onSuccess?.();
      return;
    }
  } catch (error) {
    toast.error(
      ingest
        ? ToastMessages.NAR_INGESTION_APPROVED_FAILURE
        : ToastMessages.NAR_INGESTION_FAILURE,
    );

    if (setIsSubmitting) {
      setIsSubmitting(false);
    }
  }
};
